/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, Inject } from '@angular/core';
// TODO: https://git.sys.cigna.com/angular-guild/cigna-nx/issues/567
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AnalyticsService,
  LinkType,
  SATELLITE,
} from '@cigna/shared/angular/analytics-data-access';
import { sanitizeData } from '@cigna/shared/angular/analytics-util';
import { CaRules } from './ca.rules';
import { DigitalDataObject } from './analytics/shell-track-handlers.model';
import { Satellite } from '@cigna/shared/angular/core/interfaces-util';
import { WindowService } from '@cigna/shared/angular/core/window-util';
import { CookieService } from 'ngx-cookie-service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AppstateFacade } from '@cigna/cigna-for-brokers/shell/data-access';
import { take } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const enum authStatus {
  loggedIn = 'logged in',
  loggedOut = 'logged out',
  notLoggedIn = 'not logged in',
}

@Injectable({
  providedIn: 'root',
})
export class CaAnalyticsService {
  isPublic: boolean;
  ssoId$ = this._appstateFacade.ssoId$;
  currentSsoid = '';

  constructor(
    private _analytics: AnalyticsService,
    private window: WindowService,
    @Inject(SATELLITE) private satellite: Satellite,
    private cookieService: CookieService,
    private _appstateFacade: AppstateFacade,
  ) {
    this.isPublic = this.window.location.pathname.includes('public');
    this.ssoId$.pipe(take(3)).subscribe((res: string) => {
      if (res) {
        this.currentSsoid = res;
      }
    });
  }

  public updateStore(data: DigitalDataObject) {
    this._analytics.updateStore(data);
  }

  public clearUserStore() {
    this.currentSsoid = '';
    this._analytics.updateStore({
      user: {
        profile: {
          ssoId: null,
          lobType: null,
          profileInfo: null,
        },
      },
    });
  }

  public trackEvent<R extends keyof CaRules>(
    ruleName: R,
    data: CaRules[R]['data'],
    linkType: LinkType = LinkType.OTHER,
  ) {
    this._analytics.trackEvent(this.enhanceEventData(data), linkType);
    if (data && (data.eVar14 || data.eVar99)) {
      this._analytics.updateStore({
        user: {
          profile: {
            profileInfo: {
              activeClientId: data.eVar14.replace('client:', ''),
              userType: data.eVar99,
            },
          },
        },
      });
    }
  }

  public trackSatelliteErrorImpressionEvent(
    ruleName: string,
    data: {},
    linkType: LinkType = LinkType.OTHER,
  ) {
    this.satellite?.track(
      ruleName,
      this.enhanceEventData(data),
      linkType,
      true,
    );
  }

  public trackSatelliteClickEvent(ruleName: string, data: {}) {
    this.satellite?.track(ruleName, data);
  }
  public trackSatellitePageViewEvent(ruleName: string, data: {}) {
    this.satellite?.track(ruleName, data);
  }

  public trackPage<R extends keyof CaRules>(
    ruleName: R,
    data: CaRules[R]['data'],
  ) {
    this._analytics.trackPage(sanitizeData(this.enhancePageData(data)));
    this._analytics.updateStore({
      page: {
        pageInfo: {
          channel: data && data.channel,
          hier1: data && data.hier1,
          eVar8: data && data.eVar8,
        },
      },
    });
  }
  private findUserType() {
    const pathNm = window.location.pathname;
    // for the below paths userTypeInd is empty
    if (
      pathNm.includes('/app/secure/mfa') ||
      pathNm.includes('/app/secure/useragreement') ||
      pathNm.includes('/public/app')
    ) {
      return '';
    }
    return this.cookieService.get('is-crp-internal') === 'true'
      ? 'INTERNAL'
      : String(localStorage.getItem('userTypeInd'));
  }
  private enhancePageData(data: any): object {
    return {
      ...data,
      prop67: 'page view',
      eVar126: this.findUserType(),
      prop73: new Date().toISOString(),
      ...this.includeUserData(),
    };
  }

  /*
    get the channel and hier1 of the current page (stored in digitalData object)
    append link category (optional) and link name to hier1
    getVar('name')  'name' refers to a 'Data Element' in adobe launch configuration
*/
  private enhanceEventData(data: any): object {
    const pageChannel =
      (data.channel
        ? data.channel
        : this.satellite && this.satellite.getVar('channel')) ||
      'BUG: no channel set for this page, add to routing data object';

    const pageHier1 =
      (this.satellite && this.satellite.getVar('hier1')) ||
      'BUG: no hier1 set for this page, add to routing data object';

    const linkLocation = data.prop20 ? `${data.prop20}|` : '';
    const serviceError = data.prop37 ? 'Error:' : '';

    const linkName =
      data.linkName || 'BUG: missing/empty link name from tracking call';

    const loggedIn = data.eVar8
      ? data.eVar8 // in case of logout success we pass eVar8 explicity, so this will let us use that
      : (this.satellite && this.satellite.getVar('loggedIn')) ||
        authStatus.notLoggedIn;

    return {
      ...data,
      channel: pageChannel,
      hier1: this.isPublic
        ? `${pageHier1}|${linkLocation}${serviceError}${linkName}`
        : undefined,
      eVar8: loggedIn,
      prop67: data.prop67 ? data.prop67 : 'action',
      eVar126: this.findUserType(),
      prop73: new Date().toISOString(),
      ...this.includeUserData(),
    };
  }

  private includeUserData() {
    const lobType =
      (this.satellite && this.satellite.getVar('lobType')) || null;

    return {
      eVar84: this.currentSsoid || null,
      eVar29: lobType ? `lobType:${lobType}` : null,
    };
  }
}
